import { useState, useEffect, memo } from 'react';

interface MitraGhostIconProps {
  className?: string;
  animate?: boolean;
  fast?: boolean;
  inverted?: boolean;
  whiteFill?: boolean;
}

export const MitraGhostIcon = memo(function MitraGhostIcon({ className = 'w-5 h-5', animate = false, fast = false, inverted = false, whiteFill = false }: MitraGhostIconProps) {
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  const [isBlinking, setIsBlinking] = useState(false);

  useEffect(() => {
    if (!animate) return;
    const moveMs = fast ? 800 : 2000;
    const blinkMs = fast ? 1500 : 3000;
    const moveInterval = setInterval(() => {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * 3;
      setEyeOffset({ x: Math.cos(angle) * dist, y: Math.sin(angle) * dist });
    }, moveMs);
    let blinkTimeout: ReturnType<typeof setTimeout>;
    let blinkOffTimeout: ReturnType<typeof setTimeout>;
    const scheduleBlink = () => {
      blinkTimeout = setTimeout(() => {
        setIsBlinking(true);
        blinkOffTimeout = setTimeout(() => setIsBlinking(false), 150);
        scheduleBlink();
      }, blinkMs + Math.random() * 1000);
    };
    scheduleBlink();
    return () => { clearInterval(moveInterval); clearTimeout(blinkTimeout); clearTimeout(blinkOffTimeout); };
  }, [animate, fast]);

  const floatClass = animate ? (fast ? 'animate-[mitra-float_1.5s_ease-in-out_infinite]' : 'animate-[mitra-float_3s_ease-in-out_infinite]') : undefined;
  const bodyFill = inverted ? 'white' : whiteFill ? 'white' : 'currentColor';
  const stroke = inverted ? 'white' : 'currentColor';
  const eyeFill = inverted ? 'hsl(var(--primary-500))' : 'currentColor';
  const blushFill = inverted ? 'white' : 'currentColor';
  const smileStroke = inverted ? 'hsl(var(--primary-500))' : 'currentColor';

  return (
    <svg className={className} viewBox="0 0 120 140" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g className={floatClass}>
        <path d="M20 70C20 42.4 42.4 20 70 20h0C97.6 20 110 42.4 110 70v44c0 0-4 16-16 16s-12-10-12-10-4 10-16 10-12-10-12-10-4 10-16 10-16-16-16-16V70z" fill={bodyFill} opacity={inverted ? 0.9 : whiteFill ? 0.85 : 0.15} />
        <path d="M20 70C20 42.4 42.4 20 70 20h0C97.6 20 110 42.4 110 70v44c0 0-4 16-16 16s-12-10-12-10-4 10-16 10-12-10-12-10-4 10-16 10-16-16-16-16V70z" stroke={stroke} strokeWidth="3" fill="none" opacity={inverted ? 0.4 : 1} />
        {isBlinking ? (
          <>
            <line x1="44" y1="65" x2="56" y2="65" stroke={eyeFill} strokeWidth="2.5" strokeLinecap="round" />
            <line x1="76" y1="65" x2="88" y2="65" stroke={eyeFill} strokeWidth="2.5" strokeLinecap="round" />
          </>
        ) : (
          <>
            <circle cx={50 + eyeOffset.x} cy={65 + eyeOffset.y} r="6" fill={eyeFill} style={{ transition: 'cx 0.4s ease, cy 0.4s ease' }} />
            <circle cx={82 + eyeOffset.x} cy={65 + eyeOffset.y} r="6" fill={eyeFill} style={{ transition: 'cx 0.4s ease, cy 0.4s ease' }} />
          </>
        )}
        <ellipse cx="42" cy="78" rx="7" ry="4" fill={blushFill} opacity="0.1" />
        <ellipse cx="90" cy="78" rx="7" ry="4" fill={blushFill} opacity="0.1" />
        <path d="M56 82c4 5 12 5 16 0" stroke={smileStroke} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      </g>
    </svg>
  );
});
